import type {
  AttributionStatus,
  Click,
  Currency,
  Market,
  Merchant,
  Transaction,
  TransactionStatus,
  User,
} from '../dataProvider/types';

/**
 * The Tracking diagnostics query/aggregation module (D-tracking-diagnostics, the Phase 6
 * surface-3 read-only trace) - the ONE place a member's click-to-transaction chain is
 * assembled into displayed facts. Same seam idiom as the fraud evidence panel
 * (buildFraudQueueView): v1 aggregates client-side from the mock provider, every selector
 * lives here, and the real-data migration swaps this module for a server-side endpoint
 * without the surface changing.
 *
 * STRICTLY READ-ONLY and HONEST. It shows ONLY what the data records: the member's clicks,
 * the transactions that link back to a click (via transaction.clickId), each transaction's
 * CURRENT status with its occur / confirm / expected timestamps, and the attributionStatus
 * as a fact. Three links in the real tracking chain are NOT modelled and are NEVER
 * fabricated here: the network tracking token, the vendor / network response, and the
 * state-transition HISTORY. The surface labels those as unrecorded; this module simply does
 * not invent them.
 *
 * The "missing cashback" case (a click that produced no transaction) is the CENTRE: a
 * member's converted=false clicks are surfaced first. A transaction with clickId null is
 * NOT a broken trail, it is not-click-attributed BY DESIGN, and is labelled as such.
 */

export interface TraceClick {
  id: number;
  merchantName: string;
  /** The effective cashback rate snapshotted at click time (a percentage). */
  rateSnapshot: number;
  market: Market;
  occurredAt: string;
  createdAt: string;
  converted: boolean;
}

/** The originating click of a transaction, when one is recorded. */
export interface OriginatingClick {
  id: number;
  merchantName: string;
  rateSnapshot: number;
  occurredAt: string;
}

export interface TraceTransaction {
  id: number;
  status: TransactionStatus;
  grossAmount: number;
  cashbackAmount: number;
  currency: Currency;
  market: Market;
  occurredAt: string;
  confirmedAt: string | null;
  expectedConfirmationAt: string | null;
  /** The originating click FK, or null when no click is recorded (not-click-attributed). */
  clickId: number | null;
  /** The joined originating click, or null when clickId is null or the click is absent. */
  originatingClick: OriginatingClick | null;
  attributionStatus: AttributionStatus;
  /**
   * A STATIC reading off the injected clock, NOT a history: an in-flight transaction
   * (pending / waiting) whose expectedConfirmationAt is in the past. Only the current state
   * is recorded; there is no transition timeline.
   */
  overdue: boolean;
  /** True when this is the focal transaction of a per-transaction entry. */
  focused: boolean;
}

export interface TrackingTraceMember {
  id: number;
  name: string;
  email: string;
  market: Market;
}

export interface TrackingTraceView {
  member: TrackingTraceMember;
  /** The CENTRE: the member's clicks that produced no transaction (the missing-cashback case). */
  unconvertedClicks: TraceClick[];
  /** Every click the member made, newest first. */
  clicks: TraceClick[];
  /** The member's transactions, each joined to its originating click (when recorded). */
  transactions: TraceTransaction[];
  focusTransactionId: number | null;
}

export interface TrackingTraceInput {
  user: User;
  clicks: Click[];
  transactions: Transaction[];
  merchants: Merchant[];
}

export interface TrackingTraceOptions {
  /** The injected clock, so the overdue reading is deterministic in tests. */
  now: number;
  /** A focal transaction (the per-transaction entry point), or null for the member trace. */
  focusTransactionId?: number | null;
}

const IN_FLIGHT: ReadonlySet<TransactionStatus> = new Set([
  'pending',
  'waiting',
]);

/** Newest occurredAt first, then id, so the trace reads most-recent-first. */
const newestFirst = (
  a: { occurredAt: string; id: number },
  b: typeof a,
): number => b.occurredAt.localeCompare(a.occurredAt) || b.id - a.id;

export function buildTrackingTraceView(
  input: TrackingTraceInput,
  opts: TrackingTraceOptions,
): TrackingTraceView {
  const { user, now } = { ...input, ...opts };
  const focusTransactionId = opts.focusTransactionId ?? null;
  const merchantName = new Map(input.merchants.map((m) => [m.id, m.name]));

  const memberClicks = input.clicks.filter((c) => c.userId === user.id);
  const clickById = new Map(memberClicks.map((c) => [c.id, c]));

  const toTraceClick = (c: Click): TraceClick => ({
    id: c.id,
    merchantName: merchantName.get(c.merchantId) ?? '',
    rateSnapshot: c.rateSnapshot,
    market: c.market,
    occurredAt: c.occurredAt,
    createdAt: c.createdAt,
    converted: c.converted,
  });

  const clicks = memberClicks.map(toTraceClick).sort(newestFirst);
  const unconvertedClicks = clicks.filter((c) => !c.converted);

  const transactions: TraceTransaction[] = input.transactions
    .filter((t) => t.userId === user.id)
    .map((t) => {
      const originating =
        t.clickId != null ? (clickById.get(t.clickId) ?? null) : null;
      const overdue =
        IN_FLIGHT.has(t.status) &&
        t.expectedConfirmationAt != null &&
        new Date(t.expectedConfirmationAt).getTime() < now;
      return {
        id: t.id,
        status: t.status,
        grossAmount: t.grossAmount,
        cashbackAmount: t.cashbackAmount,
        currency: t.currency,
        market: t.market,
        occurredAt: t.occurredAt,
        confirmedAt: t.confirmedAt,
        expectedConfirmationAt: t.expectedConfirmationAt,
        clickId: t.clickId,
        originatingClick: originating
          ? {
              id: originating.id,
              merchantName: merchantName.get(originating.merchantId) ?? '',
              rateSnapshot: originating.rateSnapshot,
              occurredAt: originating.occurredAt,
            }
          : null,
        attributionStatus: (t.attributionStatus ??
          'attributed') as AttributionStatus,
        overdue,
        focused: focusTransactionId != null && t.id === focusTransactionId,
      };
    })
    .sort(newestFirst);

  return {
    member: {
      id: user.id,
      name: user.name,
      email: user.email,
      market: user.market,
    },
    unconvertedClicks,
    clicks,
    transactions,
    focusTransactionId,
  };
}
